/**
 * Labels and grouping for tool steps — the agent's per-call progress, which the transcript draws
 * as activity lines between its text. Pure logic, unit-tested.
 */

import type {
    AssistantMessage, BubbleMessage, ChatMessage, ConfirmMessage, CountedPhrases, StepGroupItem,
    StepMessage, TranscriptItem,
} from '@/types/chat';

import {
    AFTER_WIDGET_PHRASES, DEFAULT_PHRASES, FAILED_PHRASES, STARTING_PHRASES, TOOL_PHRASES,
    WRITE_APPLIED_PHRASES, WRITE_APPLYING_PHRASES, WRITE_DECLINED_PHRASES, WRITE_PENDING_PHRASES,
} from './statusPhrases';

// The agent namespaces tool names with "__" (services/agent .../mcp_source.go
// nsSep): vms__, metadata__, ui__. Stripped for display.
const NS_SEP = '__';

// Tool names that read badly word for word. A change search starts at once, and its result comes
// with the step that waits for it.
const PHRASES: Record<string, string> = {
    await_change_in_video: 'find change in video',
    find_change_in_video: 'start change search',
    search_moments: 'search for moments',
    search_objects: 'search for objects',
};

// The present participle and the past tense of each verb a tool name starts with.
const VERB_FORMS: Record<string, [string, string]> = {
    add: ['adding', 'added'],
    close: ['closing', 'closed'],
    create: ['creating', 'created'],
    delete: ['deleting', 'deleted'],
    export: ['exporting', 'exported'],
    find: ['finding', 'found'],
    get: ['getting', 'got'],
    list: ['listing', 'listed'],
    navigate: ['navigating', 'navigated'],
    open: ['opening', 'opened'],
    search: ['searching', 'searched'],
    // "Seeked" is the media word, as in the video element's `seeked` event.
    seek: ['seeking', 'seeked'],
    send: ['sending', 'sent'],
    set: ['setting', 'set'],
    show: ['showing', 'showed'],
    start: ['starting', 'started'],
    update: ['updating', 'updated'],
};

// The widget a render_widget call draws right below it is the step's whole outcome.
const WIDGET_TOOL = 'render_widget';

function stripNamespace(name?: string): string {
    const rawName = name ?? '';
    const separator = rawName.indexOf(NS_SEP);
    return separator >= 0 ? rawName.slice(separator + NS_SEP.length) : rawName;
}

/**
 * stepLabel turns a namespaced tool name into a short command.
 * "metadata__search_moments" -> "Search for moments"; "ui__show_camera" -> "Show camera".
 */
export function stepLabel(name?: string): string {
    const strippedName = stripNamespace(name);
    const words = Object.hasOwn(PHRASES, strippedName)
        ? PHRASES[strippedName] : strippedName.replace(/_/g, ' ').trim();
    if (!words) { return 'Working'; }
    return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * stepTitle says what a step does or did, in the tense its status puts it:
 * "Searching for moments", "Got camera frame", "Failed to list cameras".
 */
export function stepTitle(step: StepMessage): string {
    const label = stepLabel(step.name);
    const space = label.indexOf(' ');
    const verb = (space >= 0 ? label.slice(0, space) : label).toLowerCase();
    const rest = space >= 0 ? label.slice(space) : '';
    if (!Object.hasOwn(VERB_FORMS, verb)) {
        if (step.status === 'error') { return `${label} failed`; }
        return step.status === 'interrupted' ? `${label} stopped` : label;
    }
    const [participle, pastText] = VERB_FORMS[verb] ?? ['', ''];
    switch (step.status) {
        case 'running':
            return participle.charAt(0).toUpperCase() + participle.slice(1) + rest;
        case 'done':
            return pastText.charAt(0).toUpperCase() + pastText.slice(1) + rest;
        case 'error':
            return `Failed to ${verb}${rest}`;
        case 'interrupted':
            return `Stopped ${participle}${rest}`;
    }
}

/** What a thinking reply shows when nothing says what it is thinking over. */
export const THINKING = 'Thinking…';

// The usual string-hash base: consecutive call ids land on different phrases.
const SEED_HASH_BASE = 31;

/**
 * One of `phrases`, the same one every render for the same `seed`, with `{n}` filled in.
 * A seed per call varies the wording from one call to the next.
 */
function pickPhrase(phrases: string[], seed: string, count = 0): string {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * SEED_HASH_BASE + seed.charCodeAt(i)) | 0;
    }
    const phrase = phrases[Math.abs(hash) % phrases.length];
    return phrase === undefined ? THINKING : `${phrase.replace('{n}', String(count))}…`;
}

/** The phrasings of `phrases` that fit a count, if it has any. */
function getCountedPhrases(
    phrases: CountedPhrases | undefined, count: number,
): string[] | undefined {
    return count === 1 ? phrases?.one : phrases?.many;
}

/**
 * What the model is thinking over once the steps are done: the last round's calls, by what
 * they returned. The same tool called several times side by side reads as one call returning
 * the sum: three single frames read as three frames. The agent runs them one at a time, so the
 * run's first call picks the wording, which then holds while the count climbs.
 */
export function getThinkingTitle(steps: StepMessage[]): string {
    const lastStepMessage = steps.at(-1);
    if (lastStepMessage === undefined) { return THINKING; }
    const name = stripNamespace(lastStepMessage.name);
    let firstIndex = steps.length - 1;
    while (firstIndex > 0 && stripNamespace(steps[firstIndex - 1]?.name) === name) {
        firstIndex--;
    }
    const run = steps.slice(firstIndex);
    const seed = run[0]?.stepId ?? lastStepMessage.stepId;
    const doneStepMessages = run.filter((step) => step.status === 'done');
    if (doneStepMessages.length === 0) { return pickPhrase(FAILED_PHRASES, seed); }

    const phrases = (Object.hasOwn(TOOL_PHRASES, name) ? TOOL_PHRASES[name] : undefined) ??
        DEFAULT_PHRASES;
    const images = doneStepMessages.reduce((sum, step) => sum + (step.images ?? 0), 0);
    const byImages = images > 0 ? getCountedPhrases(phrases.images, images) : undefined;
    if (byImages) { return pickPhrase(byImages, seed, images); }
    if (doneStepMessages.some((step) => step.items === undefined)) {
        return pickPhrase(phrases.any, seed);
    }
    const items = doneStepMessages.reduce((sum, step) => sum + (step.items ?? 0), 0);
    const byItems = items === 0 ? phrases.none : getCountedPhrases(phrases.items, items);
    return pickPhrase(byItems ?? phrases.any, seed, items);
}

/** What the model is doing under a write's approval card, by where the card stands. */
function confirmPhrases(card: ConfirmMessage): string[] {
    switch (card.status) {
        case 'pending':
            return WRITE_PENDING_PHRASES;
        case 'denied':
        case 'expired':
            return WRITE_DECLINED_PHRASES;
        case 'approved':
            if (card.resultError) { return FAILED_PHRASES; }
            return card.result === undefined ? WRITE_APPLYING_PHRASES : WRITE_APPLIED_PHRASES;
    }
}

/**
 * What a reply that follows no steps is thinking over: the approval card or the widget right
 * above it, else the operator's message.
 */
function getReplyThinkingTitle(reply: AssistantMessage, above?: BubbleMessage): string {
    if (above?.role === 'confirm') { return pickPhrase(confirmPhrases(above), reply.id); }
    if (above?.role === 'widget') { return pickPhrase(AFTER_WIDGET_PHRASES, reply.id); }
    return pickPhrase(STARTING_PHRASES, reply.id);
}

/**
 * The line a run of steps shows: the step in flight, else the thinking that follows the steps,
 * else the lone step, else a count.
 */
export function stepGroupTitle(steps: StepMessage[], isThinking: boolean): string {
    for (let i = steps.length - 1; i >= 0; i--) {
        const step = steps[i];
        if (step?.status === 'running') { return stepTitle(step); }
    }
    if (isThinking) { return getThinkingTitle(steps); }
    const onlyStepMessage = steps.length === 1 ? steps[0] : undefined;
    return onlyStepMessage ? stepTitle(onlyStepMessage) : `Performed ${steps.length} actions`;
}

/**
 * The call a step made, as the params of the MCP `tools/call` request: the tool's own name, which
 * its server and a UI tool's `ui_tool_call` both receive bare, and the arguments as sent.
 */
export function stepRequest(step: StepMessage): string {
    return JSON.stringify(
        { name: stripNamespace(step.name), arguments: step.args ?? {} }, null, 2);
}

/**
 * groupTranscript folds every run of steps with nothing visible between them into one item, so
 * the agent's calls between two pieces of text read as a single activity line. A change card
 * shows the search of the step line above it, so that line also says what the model thinks
 * over once the search ends.
 */
export function groupTranscript(messages: ChatMessage[]): TranscriptItem[] {
    const items: TranscriptItem[] = [];
    let group: StepGroupItem | null = null;
    let above: BubbleMessage | undefined;
    // The step line right above the card that `above` is.
    let cardGroup: StepGroupItem | null = null;
    for (const message of messages) {
        if (message.role === 'step') {
            if (stripNamespace(message.name) === WIDGET_TOOL) { continue; }
            if (group) {
                group.steps.push(message);
            } else {
                group = { kind: 'steps', id: message.id, steps: [message], isThinking: false };
                items.push(group);
            }
            continue;
        }
        if (message.role === 'assistant' && message.text.trim().length === 0) {
            // The reducer opens a bubble after every step, and one that stayed empty or got
            // only whitespace draws nothing, so it must not split the run.
            if (message.status !== 'streaming') { continue; }
            // Under steps, the steps' own line says the model is thinking.
            const host = group ?? (above?.role === 'change' ? cardGroup : null);
            if (host) {
                host.isThinking = true;
                continue;
            }
            items.push({ kind: 'message', message,
                thinking: getReplyThinkingTitle(message, above) });
            continue;
        }
        cardGroup = message.role === 'change' ? group : null;
        group = null;
        above = message;
        items.push({ kind: 'message', message });
    }
    return items;
}
